import React, { useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import Login from './component/login/login';
import CreateNew from './component/create-new/create-new';
import Dashboard from './component/dashboard/dashboard';
import Report from './component/report/report';
import DetailReports from './component/detail-reports.component/detail-reports.component';
import B2CSuccess from './component/B2CSuccess';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import EightDForms from './component/eight-dforms/eight-dforms';
import RoleMaster from './component/masters/RoleMaster';
import UserMaster from './component/masters/UserMaster';
import { useMsal } from "@azure/msal-react";
import { setEmployeeData, setDivisionList } from './services/employeeStore';
import { setCurrentUser, setSupplierEmail } from './services/auth';
import { fetchApi } from './services/api';
import IdleTimer from './sharedcomponents/idle-timer/IdleTimer';
import UserValidationService from './services/userValidationService';
import ProtectedRoute from './sharedcomponents/ProtectedRoute';
import SessionService from './services/sessionService';
import BrowserSecurityService from './services/browserSecurityService';
import GlobalInputProtection from './sharedcomponents/GlobalInputProtection/GlobalInputProtection';
import cacheControlService from './services/cacheControlService';
import httpMethodSecurity from './services/httpMethodSecurity';

function App() {
  const { instance } = useMsal();
  const navigate = useNavigate();

  /**
   * Validate user status after successful authentication
   * @param {string} userEmail - User's email address
   * @returns {Promise<boolean>} - Returns true if user is active, false otherwise
   */
  const validateUserAfterAuth = async (userEmail) => {
    try {
      const validation = await UserValidationService.validateUserStatus(userEmail);
      
      if (!validation.isActive) {
        console.warn('[App] User validation failed:', validation.error);
        
        // Show error message to user
        UserValidationService.showErrorMessage(
          validation.error || 'Your account is inactive. Please contact your administrator.'
        );
        
        // Wait a moment for user to see the message, then logout
        setTimeout(async () => {
          await UserValidationService.logoutUser(instance, navigate, validation.error);
        }, 3000);
        
        return false;
      }
      
      console.log('[App] User validation successful for:', userEmail);
      return true;
      
    } catch (error) {
      console.error('[App] User validation error:', error);
      
      // Show generic error message
      UserValidationService.showErrorMessage(
        'Unable to validate user status. Please try again or contact your administrator.'
      );
      
      // Logout user on validation failure
      setTimeout(async () => {
        await UserValidationService.logoutUser(instance, navigate, 'Validation service error');
      }, 3000);
      
      return false;
    }
  };

  const exchangeB2CToken = async (code) => {
    const clientId = process.env.REACT_APP_B2C_CLIENTID;
    const authorityDomain = process.env.REACT_APP_B2C_AUTHORITY_DOMAIN;
    const tenant = process.env.REACT_APP_B2C_TENANT;
    const policy = process.env.REACT_APP_B2C_SIGNIN_FLOW;
    const redirectUri = process.env.REACT_APP_B2C_REDIRECTURI;
    const codeVerifier = localStorage.getItem('b2c_code_verifier');
    localStorage.removeItem('b2c_code_verifier');

    const tokenUrl = `https://${authorityDomain}/${tenant}/${policy}/oauth2/v2.0/token`;
    const tokenResponse = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
        scope: `openid profile ${clientId}`
      })
    });
    return tokenResponse.json();
  };

  const handleSupplierToken = async (tokenData) => {
    if (!tokenData.id_token) return true;

    const payload = JSON.parse(atob(tokenData.id_token.split('.')[1]));
    const supplierEmail = payload.emails?.[0] || payload.email || payload.preferred_username || payload.sub;

    if (supplierEmail) {
      setSupplierEmail(supplierEmail);
      setCurrentUser(supplierEmail, tokenData.access_token || '', supplierEmail);
      const isValidUser = await validateUserAfterAuth(supplierEmail);
      if (!isValidUser) return false;
      
      // Initialize security services after successful supplier login
      BrowserSecurityService.initialize();
      cacheControlService.initialize();
      httpMethodSecurity.initialize();
    } else if (tokenData.access_token) {
      sessionStorage.setItem('userToken', tokenData.access_token);
    }
    return true;
  };;

  const handleSupplierCallback = async (urlParams) => {
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (code) {
      sessionStorage.setItem('b2c_auth_code', code);
      sessionStorage.setItem('user_type', 'supplier');
      try {
        const tokenData = await exchangeB2CToken(code);
        console.log('[Supplier logic] B2C token response:', tokenData);
        const isValid = await handleSupplierToken(tokenData);
        if (!isValid) return;

        // Register session to prevent concurrent logins for suppliers
        const supplierEmail = sessionStorage.getItem('userEmail');
        if (supplierEmail) {
          await SessionService.registerSession(supplierEmail);
          SessionService.startSessionValidation(supplierEmail, () => {
            SessionService.handleConcurrentLogin(instance, navigate);
          });
        }
      } catch (tokenError) {
        console.error('[Supplier logic] B2C token exchange failed:', tokenError);
      }
      navigate("/dashboard", { replace: true });
    } else if (error) {
      console.error('B2C authentication error:', error);
      navigate("/login", { replace: true });
    }
  };

  const fetchAndStoreEmployeeData = async (userEmail) => {
    try {
      const employeeData = await fetchApi('/api/UserMaster/GetUserDetailsByEmail', {
        method: 'POST',
        body: JSON.stringify({ 
          EmailID: userEmail.split('@')[0], // Remove domain for the search
          Email: userEmail // Also send full email as fallback
        }),
        skipLoader: true
      });

      const data = Array.isArray(employeeData?.data) && employeeData.data.length > 0 
        ? employeeData.data[0] 
        : employeeData?.data || employeeData;
      
      if (data) {
        setEmployeeData(data);

        // Extract division information
        const divisions = [];
        if (data.Division_Code) divisions.push(data.Division_Code);
        if (data.divCode) divisions.push(data.divCode);
        
        if (divisions.length > 0) {
          setDivisionList([...new Set(divisions)]);
        }
      }
    } catch (apiError) {
      console.error("Employee API call failed:", apiError);
      // Don't fail the entire login process if employee data fetch fails
      console.warn("Continuing without employee data - user can still access the application");
    }
  };

  const handleSSOResponse = async (response) => {
    const userEmail = response.account.username;
    setCurrentUser(userEmail, response.accessToken, response.account.name);

    const isValidUser = await validateUserAfterAuth(userEmail);
    if (!isValidUser) return;

    // Initialize security services after successful login
    BrowserSecurityService.initialize();
    cacheControlService.initialize();
    httpMethodSecurity.initialize();

    // Register session to prevent concurrent logins
    await SessionService.registerSession(userEmail);
    SessionService.startSessionValidation(userEmail, () => {
      SessionService.handleConcurrentLogin(instance, navigate);
    });

    await fetchAndStoreEmployeeData(userEmail);
    navigate("/dashboard", { replace: true });
  };;

  const handleExistingSession = async () => {
    const accounts = instance.getAllAccounts();
    const isSupplier = sessionStorage.getItem('user_type') === 'supplier';

    if (accounts.length === 0 && !isSupplier) {
      navigate("/login", { replace: true });
      return;
    }

    const existingEmail = sessionStorage.getItem('userEmail');
    if (existingEmail) {
      const isValidUser = await validateUserAfterAuth(existingEmail);
      if (!isValidUser) return;

      // Validate existing session against server
      const sessionValid = await SessionService.validateSession(existingEmail);
      if (!sessionValid) {
        SessionService.handleConcurrentLogin(instance, navigate);
        return;
      }
      SessionService.startSessionValidation(existingEmail, () => {
        SessionService.handleConcurrentLogin(instance, navigate);
      });

      // Re-populate employee store on refresh (lost because it's in-memory only)
      await fetchAndStoreEmployeeData(existingEmail);

      // Re-fetch user access after token is confirmed valid (fixes nav on refresh)
      const { fetchUserAccess } = await import('./services/roleAccessService');
      await fetchUserAccess();
    }

    if (window.location.pathname === '/' || window.location.pathname === '/login') {
      navigate("/dashboard", { replace: true });
    }
  };

  useEffect(() => {
    // Don't initialize security services during any login flow (SSO/B2C)
    const isSSO = window.location.search.includes('code=') || window.location.search.includes('state=');
    const isLogin = window.location.pathname === '/login';
    const isB2C = window.location.pathname === '/b2c-success' || sessionStorage.getItem('b2c_auth_code');
    const hasError = window.location.search.includes('error=');
    
    if (!isLogin && !isSSO && !isB2C && !hasError) {
      // Only initialize if user is already authenticated
      const userEmail = sessionStorage.getItem('userEmail');
      const userToken = sessionStorage.getItem('userToken');
      
      if (userEmail && userToken) {
        // Initialize browser security service
        BrowserSecurityService.initialize();
        
        // Initialize cache control service
        cacheControlService.initialize();
        
        // Initialize HTTP method security
        httpMethodSecurity.initialize();
        
        // Initialize Slowloris protection only after authentication is complete
        if (window.slowlorisProtection) {
          console.log('[App] Slowloris protection initialized after authentication');
        }
      }
    }
    
    const handleRedirect = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('state') === 'supplier_login') {
          await handleSupplierCallback(urlParams);
          return;
        }

        const response = await instance.handleRedirectPromise();
        if (response) {
          await handleSSOResponse(response);
          return;
        }

        await handleExistingSession();
      } catch (error) {
        // Handle MSAL cached authority error gracefully - retry with fresh login
        if (error.errorCode === 'no_cached_authority_error') {
          console.warn('[App] No cached authority found, redirecting to login');
          navigate("/login", { replace: true });
          return;
        }
        console.error("Redirect handling failed:", error);
      }
    };
    handleRedirect();
  }, [instance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => SessionService.stopSessionValidation();
  }, []);

  return (
    <GlobalInputProtection>
      <IdleTimer>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/b2c-success" element={<B2CSuccess />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/reports" element={<ProtectedRoute path="reports"><Report /></ProtectedRoute>} />
          <Route path="/8D-forms" element={<ProtectedRoute path="8d-forms"><EightDForms /></ProtectedRoute>} />
          <Route path="/eight-dforms" element={<ProtectedRoute path="8d-forms"><EightDForms /></ProtectedRoute>} />
          <Route path="/create-new" element={<ProtectedRoute path="create-new"><CreateNew /></ProtectedRoute>} />
          <Route path="/detail-reports" element={<ProtectedRoute path="reports"><DetailReports /></ProtectedRoute>} />
          <Route path="/masters/role-master" element={<ProtectedRoute path="masters/role-master"><RoleMaster /></ProtectedRoute>} />
          <Route path="/masters/user-master" element={<ProtectedRoute path="masters/user-master"><UserMaster /></ProtectedRoute>} />
          <Route path="*" element={null} />
        </Routes>
      </IdleTimer>
    </GlobalInputProtection>
  );
}

export default App;


